import { decodeEventLog, encodeFunctionData, isAddress, parseAbi, toEventSelector, zeroAddress,
  type Address, type Hex, type TransactionReceipt } from 'viem';
import { z } from 'zod';

// Version-specific experimental ABI. Not a production deployment/profile approval.
export const experimentalConditionalOrderAbi = parseAbi([
  'function fill(uint256 id,uint128 maximumThisFill,uint256 expectedRevision)',
  'event Filled(uint256 indexed id,uint256 revision,uint256 actualInput,uint256 actualOutput,uint256 cumulativeInput,uint256 cumulativeOutput,bool complete)',
]);
export const experimentalTriggerOrderAbi = [...experimentalConditionalOrderAbi, ...parseAbi([
  'function registerConditional(bool buy,uint128 maximumInput,uint128 minimumTotalOutput,uint16 maximumFeeBps,uint256 expiry,(uint256 threshold,bool atOrBelow,bool latchAfterFirstFill) terms) returns(uint256 id)',
  'function triggers(uint256) view returns(uint256 threshold,bool atOrBelow,bool latchAfterFirstFill)',
  'function currentTriggerValuation() view returns(uint256)',
  'function checkCurveTrigger(uint256 id,uint256 revision) view',
  'function checkCanonicalTrigger(address payer,bytes32 poolId,uint256 id,uint256 revision) view returns(bytes4)',
])] as const;
const uint256 = z.bigint().min(0n).max((1n << 256n) - 1n);
const positive128 = z.bigint().min(1n).max((1n << 128n) - 1n);
const address = z.custom<Address>(value => typeof value === 'string' && isAddress(value, { strict: false }) && value.toLowerCase() !== zeroAddress);
const hash = z.custom<Hex>(value => typeof value === 'string' && /^0x[0-9a-f]{64}$/i.test(value));
const expectation = z.object({
  version: z.enum(['conditional-curve-order.2/conditional-launch-order.1', 'conditional-trigger-order.1', 'conditional-linked-allocation.1']), chainId: z.literal(4663),
  engine: address, keeper: address, transactionHash: hash, transactionNonce: uint256,
  orderId: uint256.refine(value => value > 0n), maximumThisFill: positive128, expectedRevision: uint256,
  maximumInput: positive128, minimumTotalOutput: positive128,
  previousInput: uint256, previousOutput: uint256,
}).strict();
export type ExperimentalOrderFillExpectation = z.infer<typeof expectation>;
export type ExperimentalOrderFillTransaction = {
  hash: Hex; blockHash: Hex | null; blockNumber: bigint | null; transactionIndex: number | null;
  from: Address; to: Address | null; input: Hex; value: bigint; nonce: number;
};
const eq = (a: string | null | undefined, b: string) => typeof a === 'string' && a.toLowerCase() === b.toLowerCase();
const index = (value: unknown): value is number => Number.isSafeInteger(value) && (value as number) >= 0;

/** Pure structural receipt attribution for the exact experimental engine versions.
 * Expected attempt/previous counters must be independently restored from the journal,
 * never inferred from these logs. This does NOT verify deployed code, owner authority,
 * trigger conditions, token balance deltas, gas budget or canonicality/finality.
 * The linked-allocation version shares the per-leg Filled layout. This matcher does
 * NOT verify its peer linkage or shared remainder; inspect those separately.
 * No state writes or resubmission permission. A durable consumer must atomically fence
 * the original revision and deduplicate (chain, transactionHash, logIndex), with reorgs
 * handled separately. Keeper transactions here call fill directly, not via a relayer.
 */
export function matchExperimentalConditionalOrderFill(input: {
  expected: ExperimentalOrderFillExpectation; transaction: ExperimentalOrderFillTransaction;
  receipt: TransactionReceipt;
}) {
  const e = expectation.parse(input.expected);
  const t = input.transaction, r = input.receipt;
  if (e.previousInput >= e.maximumInput || e.maximumThisFill > e.maximumInput - e.previousInput
    || (e.previousInput === 0n ? e.previousOutput !== 0n || e.expectedRevision !== 0n
      : e.expectedRevision === 0n || e.expectedRevision > e.previousInput
        || e.previousOutput * e.maximumInput < e.previousInput * e.minimumTotalOutput))
    throw new Error('Invalid saved order progress');
  hash.parse(r.blockHash); hash.parse(r.transactionHash); uint256.parse(r.blockNumber);
  if (!index(r.transactionIndex) || !index(t.nonce) || BigInt(t.nonce) !== e.transactionNonce
    || !eq(t.hash, e.transactionHash) || !eq(r.transactionHash, e.transactionHash)
    || !eq(t.blockHash, r.blockHash) || t.blockNumber !== r.blockNumber || t.transactionIndex !== r.transactionIndex
    || !eq(t.from, e.keeper) || !eq(r.from, e.keeper) || !eq(t.to, e.engine) || !eq(r.to, e.engine)
    || t.value !== 0n || r.status !== 'success') throw new Error('Order fill transaction identity/outcome mismatch');
  const call = encodeFunctionData({ abi: experimentalConditionalOrderAbi, functionName: 'fill',
    args: [e.orderId, e.maximumThisFill, e.expectedRevision] });
  if (!eq(t.input, call)) throw new Error('Order fill calldata differs from saved attempt');
  let lastIndex = -1;
  for (const log of r.logs) {
    if (!index(log.logIndex) || log.logIndex <= lastIndex || log.removed !== false
      || !eq(log.blockHash, r.blockHash) || log.blockNumber !== r.blockNumber
      || !eq(log.transactionHash, r.transactionHash) || log.transactionIndex !== r.transactionIndex)
      throw new Error('Order fill log receipt identity/order mismatch');
    lastIndex = log.logIndex;
  }
  const entries = r.logs.filter(log => eq(log.address, e.engine));
  if (entries.length !== 1) throw new Error('Exactly one engine Filled event required');
  const log = entries[0]!;
  if (log.topics.length !== 2 || !eq(log.topics[0], toEventSelector(experimentalConditionalOrderAbi[1]))
    || !/^0x[0-9a-f]{384}$/i.test(log.data)) throw new Error('Malformed engine Filled event');
  hash.parse(log.topics[1]);
  const { args: f } = decodeEventLog({ abi: experimentalConditionalOrderAbi, eventName: 'Filled',
    topics: log.topics, data: log.data, strict: true });
  // Enforce canonical ABI bytes too: some decoders accept noncanonical boolean words.
  if (!/^0{63}[01]$/i.test(log.data.slice(-64))) throw new Error('Noncanonical Filled completion flag');
  if (f.id !== e.orderId || f.revision !== e.expectedRevision + 1n || f.actualInput === 0n
    || f.actualInput > e.maximumThisFill || f.actualOutput === 0n
    || f.cumulativeInput !== e.previousInput + f.actualInput || f.cumulativeInput > e.maximumInput
    || f.cumulativeOutput !== e.previousOutput + f.actualOutput
    || f.actualOutput * e.maximumInput < f.actualInput * e.minimumTotalOutput
    || f.complete !== (f.cumulativeInput === e.maximumInput)) throw new Error('Filled event violates original order bounds/progress');
  return {
    schemaVersion: 1 as const, source: 'experimental-order-fill-structural-match' as const,
    accountingEligible: false as const, resubmissionAuthorized: false as const,
    verification: 'historical-code-authority-balances-and-canonicality-required' as const,
    chainId: e.chainId, engine: e.engine, orderId: e.orderId,
    ...f, transactionHash: r.transactionHash, blockHash: r.blockHash, blockNumber: r.blockNumber,
    transactionIndex: r.transactionIndex, logIndex: log.logIndex!,
  };
}
